import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/auction/StatusBadge";
import { CountdownPill } from "@/components/auction/CountdownPill";
import { ShipmentTracker, ReturnStatusBadge } from "@/components/orders/ShipmentTracker";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
    Sparkles, Plus, ShieldCheck, Wallet, Loader2, Store, Lock, Mail, Phone,
    FlaskConical, CheckCircle2, Clock, ArrowRight, Banknote, TrendingUp, Trophy,
    Truck, Package, Upload, X, BadgeDollarSign, RotateCcw, UserCircle, Image as ImageIcon, ExternalLink,
} from "lucide-react";

const cx = (...a) => a.filter(Boolean).join(" ");

// Small inline indicator that a flow is running in simulated/test mode.
function SimBadge({ label = "Simulated mode" }) {
    return (
        <span data-testid="sim-mode-badge" className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2.5 py-0.5 text-[11px] font-medium text-warning">
            <FlaskConical className="h-3 w-3" />{label}
        </span>
    );
}

const CONDITIONS = [
    "New",
    "New with tags",
    "New (open box)",
    "Mint condition",
    "Like new",
    "Refurbished",
    "Certified pre-owned",
    "Used - Excellent",
    "Used - Good",
    "Used - Fair",
    "Poor condition",
    "For parts / not working",
];

const PAYOUT_TONE = {
    PENDING: "bg-warning/10 text-warning border-warning/30",
    PROCESSING: "bg-warning/10 text-warning border-warning/30",
    READY: "bg-electric/10 text-electric border-electric/30",
    PAID: "bg-success/10 text-success border-success/30",
    CANCELLED: "bg-destructive/10 text-destructive border-destructive/30",
};

function PayoutBadge({ status }) {
    return (
        <span className={cx("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium", PAYOUT_TONE[status] || "bg-secondary text-secondary-foreground border-border")}>
            {String(status || "").replace(/_/g, " ")}
        </span>
    );
}

export default function SellerDashboard() {
    const { user, loading, refreshUser, config } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [cats, setCats] = useState([]);
    const [myAuctions, setMyAuctions] = useState(null);
    const [payouts, setPayouts] = useState(null);

    const tab = params.get("tab") || "auctions";
    const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n); };

    useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
    useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => setCats([])); }, []);

    const load = useCallback(() => {
        api.get("/me/auctions").then(({ data }) => setMyAuctions(data)).catch(() => setMyAuctions([]));
        api.get("/seller/payouts").then(({ data }) => setPayouts(data)).catch(() => setPayouts({ payouts: [], total_net: 0, mode: "simulated" }));
    }, []);
    useEffect(() => { if (user) load(); }, [user, load]);

    // Handle Stripe Connect return:
    //  • ?connect=complete -> seller returned from hosted onboarding; sync status
    //  • ?connect=refresh  -> onboarding link expired; re-issue a fresh one
    useEffect(() => {
        const cs = params.get("connect");
        if (!user) return;
        if (cs === "complete") {
            api.post("/seller/connect/complete")
                .then(({ data }) => {
                    refreshUser();
                    if (data?.seller_status === "VERIFIED") toast.success("Seller verification complete");
                    else toast.info("Onboarding submitted — Stripe is reviewing your details.");
                })
                .catch(() => {});
        } else if (cs === "refresh") {
            api.post("/seller/connect/onboard")
                .then(({ data }) => { if (data?.mode === "real" && data?.url) window.location.href = data.url; })
                .catch(() => {});
        }
    }, [params, user, refreshUser]);

    if (!user) return null;
    const verified = user.seller_status === "VERIFIED";

    return (
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">Seller dashboard</h1>
                    <p className="text-sm text-muted-foreground">Create listings, track auctions and payouts.</p>
                </div>
                {verified
                    ? <span data-testid="seller-verified-badge" className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-3 py-1 text-xs font-medium text-success"><ShieldCheck className="h-3.5 w-3.5" />Verified seller</span>
                    : <span data-testid="seller-unverified-badge" className="rounded-full border border-warning/40 bg-warning/10 px-3 py-1 text-xs text-warning">Verification required</span>}
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-6">
                <TabsList>
                    <TabsTrigger value="auctions" data-testid="seller-tab-auctions">My auctions</TabsTrigger>
                    <TabsTrigger value="create" data-testid="seller-tab-create">Create</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="seller-tab-orders">Orders / Shipping</TabsTrigger>
                    <TabsTrigger value="profile" data-testid="seller-tab-profile">Profile</TabsTrigger>
                    <TabsTrigger value="payouts" data-testid="seller-tab-payouts">Payouts</TabsTrigger>
                    <TabsTrigger value="verification" data-testid="seller-tab-verification">Verification</TabsTrigger>
                </TabsList>

                <TabsContent value="auctions" className="mt-4">
                    <MyAuctionsTab myAuctions={myAuctions} onCreate={() => setTab("create")} />
                </TabsContent>
                <TabsContent value="create" className="mt-4">
                    <CreateTab verified={verified} cats={cats} onCreated={() => { load(); setTab("auctions"); }} onGoVerify={() => setTab("verification")} />
                </TabsContent>
                <TabsContent value="orders" className="mt-4">
                    <OrdersTab />
                </TabsContent>
                <TabsContent value="profile" className="mt-4">
                    <ProfileTab user={user} />
                </TabsContent>
                <TabsContent value="payouts" className="mt-4">
                    <PayoutsTab payouts={payouts} config={config} />
                </TabsContent>
                <TabsContent value="verification" className="mt-4">
                    <VerificationTab user={user} verified={verified} config={config} onManage={() => navigate("/account/verify")} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// ----------------- My auctions -----------------
function MyAuctionsTab({ myAuctions, onCreate }) {
    if (myAuctions === null) return <Skeleton className="h-48 w-full rounded-xl" />;
    if (myAuctions.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
                <Store className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-3 font-display font-semibold">No listings yet</p>
                <p className="mt-1 text-sm text-muted-foreground">Create your first sealed-bid auction to start selling.</p>
                <Button className="mt-4 rounded-[10px]" onClick={onCreate} data-testid="seller-create-cta"><Plus className="mr-2 h-4 w-4" />Create listing</Button>
            </div>
        );
    }
    return (
        <div className="grid gap-3">
            {myAuctions.map((a) => (
                <Card key={a.id} data-testid="seller-auction-row" className="flex flex-wrap items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-3">
                        <img src={a.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover" />
                        <div>
                            <Link to={`/auctions/${a.id}`} className="font-display font-semibold hover:underline">{a.title}</Link>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                <StatusBadge status={a.status} />
                                <CountdownPill endTime={a.end_time} status={a.status} />
                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" />{a.unique_bidder_count}/{a.max_bidders} bidders</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        {a.winning_amount != null ? (
                            <>
                                <p className="text-xs text-muted-foreground">Final price</p>
                                <p className="font-display text-lg font-semibold tabular-nums text-brass">{money(a.winning_amount)}</p>
                                {a.payout_status && <PayoutBadge status={a.payout_status} />}
                            </>
                        ) : (
                            <>
                                <p className="text-xs text-muted-foreground">Starting at</p>
                                <p className="font-display text-lg font-semibold tabular-nums">{money(a.starting_price)}</p>
                            </>
                        )}
                    </div>
                </Card>
            ))}
        </div>
    );
}

// ----------------- Create (gated by verification) -----------------
function CreateTab({ verified, cats, onCreated, onGoVerify }) {
    if (!verified) {
        return (
            <Card className="flex flex-col items-center gap-3 p-12 text-center" data-testid="create-locked">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-warning/10 text-warning"><Lock className="h-6 w-6" /></span>
                <h3 className="font-display text-lg font-semibold">Complete seller verification to list</h3>
                <p className="max-w-md text-sm text-muted-foreground">You need a verified email, phone, and seller identity (Stripe Connect) before publishing auctions. It only takes a minute.</p>
                <Button className="rounded-[10px]" onClick={onGoVerify} data-testid="create-go-verify"><ShieldCheck className="mr-2 h-4 w-4" />Go to verification</Button>
            </Card>
        );
    }
    return <CreateListing cats={cats} onCreated={onCreated} />;
}

function CreateListing({ cats, onCreated }) {
    const [form, setForm] = useState({ title: "", description: "", category: "", condition: "Used - Good", starting_price: "", max_bidders: "5", duration_days: "2", images: "" });
    const [aiBusy, setAiBusy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [uploaded, setUploaded] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPickFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        for (const f of files) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post("/uploads/image", fd);
                setUploaded((prev) => [...prev, data.url]);
            } catch (err) {
                toast.error(err?.response?.data?.detail || `Could not upload ${f.name}`);
            }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };
    const removeUploaded = (url) => setUploaded((prev) => prev.filter((u) => u !== url));

    const genAI = async () => {
        if (!form.title) { toast.error("Enter a title first"); return; }
        setAiBusy(true);
        try {
            const { data } = await api.post("/ai/generate-description", { title: form.title, category: form.category, condition: form.condition, details: form.description });
            set("description", data.description);
            toast.success("AI description generated");
        } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); } finally { setAiBusy(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const typed = form.images.split(",").map((s) => s.trim()).filter(Boolean);
            const images = [...uploaded, ...typed];
            if (images.length === 0) { toast.error("Add at least one photo (upload or URL)"); setBusy(false); return; }
            await api.post("/auctions", {
                title: form.title, description: form.description, category: form.category,
                condition: form.condition, images,
                starting_price: parseFloat(form.starting_price), max_bidders: parseInt(form.max_bidders, 10),
                duration_hours: parseFloat(form.duration_days) * 24,
            });
            toast.success("Listing published");
            onCreated();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create listing"); } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="grid gap-6 lg:grid-cols-2">
            <Card className="space-y-5 p-6 sm:p-7">
                <div className="space-y-2.5"><Label htmlFor="title">Title</Label><Input id="title" data-testid="listing-title" value={form.title} onChange={(e) => set("title", e.target.value)} required /></div>
                <div className="space-y-2.5">
                    <div className="flex items-center justify-between"><Label htmlFor="desc">Description</Label>
                        <Button type="button" size="sm" variant="secondary" className="rounded-[10px]" onClick={genAI} disabled={aiBusy} data-testid="ai-generate-button">{aiBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}AI assist</Button>
                    </div>
                    <Textarea id="desc" data-testid="listing-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2.5"><Label>Category</Label>
                        <Select value={form.category} onValueChange={(v) => set("category", v)}>
                            <SelectTrigger data-testid="listing-category"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2.5"><Label htmlFor="cond">Condition</Label>
                        <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                            <SelectTrigger id="cond" data-testid="listing-condition"><SelectValue placeholder="Select condition" /></SelectTrigger>
                            <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>Photos</Label>
                    <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid="listing-file-input" />
                    <Button type="button" variant="secondary" className="w-full rounded-[10px]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="listing-upload-button">
                        {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        {uploading ? "Uploading…" : "Upload photos"}
                    </Button>
                    {uploaded.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 pt-1" data-testid="listing-upload-previews">
                            {uploaded.map((url) => (
                                <div key={url} className="group relative aspect-square overflow-hidden rounded-lg border border-border">
                                    <img src={url} alt="upload preview" className="h-full w-full object-cover" />
                                    <button type="button" onClick={() => removeUploaded(url)} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-background/80 text-destructive opacity-0 transition-opacity group-hover:opacity-100" data-testid="listing-upload-remove" aria-label="Remove photo">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="space-y-2"><Label htmlFor="imgs">Or paste image URLs (comma-separated)</Label><Input id="imgs" data-testid="listing-images" value={form.images} onChange={(e) => set("images", e.target.value)} placeholder="https://..." /></div>
            </Card>
            <Card className="space-y-5 p-6 sm:p-7">
                <div className="space-y-2.5"><Label htmlFor="price">Starting price (USD)</Label><Input id="price" data-testid="listing-price" type="number" min="1" step="0.01" value={form.starting_price} onChange={(e) => set("starting_price", e.target.value)} required /></div>
                <div className="space-y-2.5"><Label htmlFor="maxb">Max bidders (auction closes when reached)</Label><Input id="maxb" data-testid="listing-maxbidders" type="number" min="2" value={form.max_bidders} onChange={(e) => set("max_bidders", e.target.value)} required /></div>
                <div className="space-y-2.5"><Label htmlFor="dur">Duration (days)</Label><Input id="dur" data-testid="listing-duration" type="number" min="1" max="30" step="1" value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)} required /></div>
                <div className="rounded-lg border border-border bg-muted/50 p-4 text-xs leading-relaxed text-muted-foreground">
                    Your auction will close automatically when the timer expires <strong>or</strong> when {form.max_bidders || "N"} unique bidders have placed sealed bids — whichever happens first.
                </div>
                <Button type="submit" className="w-full rounded-[10px]" disabled={busy} data-testid="listing-publish">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Plus className="mr-2 h-4 w-4" />Publish listing</Button>
            </Card>
        </form>
    );
}

// ----------------- Orders / Shipping -----------------
function OrdersTab() {
    const [orders, setOrders] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        let active = true;
        api.get("/seller/orders")
            .then(({ data }) => { if (active) setOrders(data); })
            .catch(() => { if (active) { setOrders([]); toast.error("Failed to load orders"); } });
        return () => { active = false; };
    }, [reloadKey]);

    if (orders === null) return <Skeleton className="h-48 w-full rounded-xl" />;
    if (orders.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-border p-12 text-center" data-testid="seller-orders-empty">
                <Package className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-3 font-display font-semibold">No orders yet</p>
                <p className="mt-1 text-sm text-muted-foreground">When a buyer pays for a won item, it appears here ready to ship.</p>
            </div>
        );
    }
    return (
        <div className="grid gap-4">
            {orders.map((o) => <SellerOrderCard key={o.auction_id} order={o} onChange={reload} />)}
        </div>
    );
}

function SellerOrderCard({ order, onChange }) {
    const [shipOpen, setShipOpen] = useState(false);
    const [carrier, setCarrier] = useState("AUTO");
    const [busy, setBusy] = useState("");
    const [denyOpen, setDenyOpen] = useState(false);
    const [denyNote, setDenyNote] = useState("");
    const r = order.return;

    const doShip = async () => {
        setBusy("ship");
        try {
            await api.post(`/seller/orders/${order.auction_id}/ship`, carrier === "AUTO" ? {} : { carrier });
            toast.success("Shipment created — label purchased");
            setShipOpen(false); onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create shipment"); } finally { setBusy(""); }
    };
    const respond = async (action, note = "") => {
        setBusy(action);
        try {
            await api.post(`/seller/returns/${r.id}/respond`, { action, note });
            toast.success(action === "approve" ? "Return approved" : "Return denied");
            setDenyOpen(false); setDenyNote(""); onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
    };
    const refund = async () => {
        setBusy("refund");
        try {
            await api.post(`/seller/returns/${r.id}/refund`);
            toast.success("Refund issued");
            onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); } finally { setBusy(""); }
    };

    return (
        <Card data-testid="seller-order-row" className="space-y-4 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <img src={order.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                    <div>
                        <Link to={`/auctions/${order.auction_id}`} className="font-display font-semibold hover:underline">{order.title}</Link>
                        <p className="text-xs text-muted-foreground">Buyer: {order.buyer_name} · Sold for <span className="font-medium text-brass">{money(order.winning_amount)}</span></p>
                    </div>
                </div>
                {!order.shipment ? (
                    <Button className="rounded-[10px]" onClick={() => setShipOpen(true)} data-testid="seller-ship-button"><Truck className="mr-2 h-4 w-4" />Ship item</Button>
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success"><CheckCircle2 className="h-4 w-4" />Shipped</span>
                )}
            </div>

            {order.shipment && <ShipmentTracker shipment={order.shipment} />}

            {r && (
                <div className="rounded-xl border border-warning/30 bg-warning/[0.06] p-4" data-testid="seller-return-panel">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <RotateCcw className="h-4 w-4 text-warning" />
                            <span className="text-sm font-semibold">Return request</span>
                        </div>
                        <ReturnStatusBadge status={r.status} />
                    </div>
                    <p className="mt-2 text-sm"><span className="font-medium">{r.reason}</span>{r.details ? ` — ${r.details}` : ""}</p>
                    {r.photos?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {r.photos.map((p) => <img key={p} src={p} alt="return" className="h-14 w-14 rounded-md border border-border object-cover" />)}
                        </div>
                    )}
                    {r.return_tracking_number && <p className="mt-2 text-xs text-muted-foreground">Return label: {r.return_carrier} · {r.return_tracking_number}</p>}

                    {r.status === "REQUESTED" && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Button size="sm" className="rounded-[10px]" disabled={busy === "approve"} onClick={() => respond("approve")} data-testid="return-approve-button">{busy === "approve" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Approve return</Button>
                            <Button size="sm" variant="destructive" className="rounded-[10px]" onClick={() => setDenyOpen(true)} data-testid="return-deny-button">Deny</Button>
                        </div>
                    )}
                    {["APPROVED", "RETURN_SHIPPED"].includes(r.status) && (
                        <Button size="sm" className="mt-3 rounded-[10px]" disabled={busy === "refund"} onClick={refund} data-testid="return-refund-button">{busy === "refund" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <BadgeDollarSign className="mr-1.5 h-3.5 w-3.5" />}Issue refund</Button>
                    )}
                </div>
            )}

            {/* Ship dialog */}
            <Dialog open={shipOpen} onOpenChange={setShipOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Ship order</DialogTitle>
                        <DialogDescription>Buy a shipping label and start tracking. In simulated mode the label cost is deducted from your payout.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label className="text-xs">Carrier</Label>
                        <Select value={carrier} onValueChange={setCarrier}>
                            <SelectTrigger data-testid="ship-carrier-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="AUTO">Auto-select cheapest</SelectItem>
                                <SelectItem value="UPS">UPS</SelectItem>
                                <SelectItem value="FedEx">FedEx</SelectItem>
                                <SelectItem value="USPS">USPS</SelectItem>
                                <SelectItem value="DHL Express">DHL Express</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
                            <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>Simulated shipping: a tracking number and live status timeline are generated automatically. Add real carrier keys later for live labels.</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button className="rounded-[10px]" disabled={busy === "ship"} onClick={doShip} data-testid="ship-confirm-button">{busy === "ship" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Truck className="mr-2 h-4 w-4" />}Buy label &amp; ship</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Deny dialog */}
            <Dialog open={denyOpen} onOpenChange={setDenyOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Deny return</DialogTitle></DialogHeader>
                    <Textarea value={denyNote} onChange={(e) => setDenyNote(e.target.value)} placeholder="Reason shared with the buyer (optional)" data-testid="return-deny-note" />
                    <DialogFooter><Button variant="destructive" className="rounded-[10px]" disabled={busy === "deny"} onClick={() => respond("deny", denyNote)} data-testid="return-deny-confirm">Deny return</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

// ----------------- Payouts -----------------
function PayoutsTab({ payouts, config }) {
    if (payouts === null) return <Skeleton className="h-48 w-full rounded-xl" />;
    const list = payouts.payouts || [];
    const real = payouts.mode === "real" || config?.stripe_connect_real;
    const feePct = config?.platform_fee_pct ?? 8;

    const sum = (statuses) => list.filter((p) => statuses.includes(p.status)).reduce((t, p) => t + Number(p.net || 0), 0);
    const pendingTotal = sum(["PENDING", "PROCESSING", "READY"]);
    const paidTotal = sum(["PAID"]);

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-electric" />
                    <h2 className="font-display text-lg font-semibold">Payouts</h2>
                </div>
                {real ? <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2.5 py-0.5 text-[11px] font-medium text-success"><CheckCircle2 className="h-3 w-3" />Stripe Connect live</span> : <SimBadge label="Payouts simulated" />}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
                <Card className="p-5" data-testid="payout-stat-pending">
                    <div className="flex items-center justify-between"><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Awaiting payout</p><Clock className="h-4 w-4 text-warning" /></div>
                    <p className="mt-2 font-display text-2xl font-bold tabular-nums">{money(pendingTotal)}</p>
                </Card>
                <Card className="p-5" data-testid="payout-stat-paid">
                    <div className="flex items-center justify-between"><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Paid out</p><Banknote className="h-4 w-4 text-success" /></div>
                    <p className="mt-2 font-display text-2xl font-bold tabular-nums">{money(paidTotal)}</p>
                </Card>
                <Card className="p-5" data-testid="payout-stat-fee">
                    <div className="flex items-center justify-between"><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Marketplace fee</p><TrendingUp className="h-4 w-4 text-electric" /></div>
                    <p className="mt-2 font-display text-2xl font-bold tabular-nums">{feePct}%</p>
                </Card>
            </div>

            {list.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-12 text-center">
                    <Banknote className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-3 font-display font-semibold">No payouts yet</p>
                    <p className="mt-1 text-sm text-muted-foreground">When a buyer pays for your won item, your net payout appears here.{!real && " Settlement is simulated until Stripe Connect keys are added."}</p>
                </div>
            ) : (
                <div className="grid gap-3">
                    {list.map((p) => (
                        <Card key={p.id} data-testid="payout-row" className="flex flex-wrap items-center justify-between gap-4 p-4">
                            <div>
                                <p className="font-display font-semibold">{p.auction_title}</p>
                                <p className="text-xs text-muted-foreground">Gross {money(p.gross)} · Fee {money(p.fee)}</p>
                            </div>
                            <div className="flex items-center gap-4 text-right">
                                <div>
                                    <p className="text-xs text-muted-foreground">Net payout</p>
                                    <p className="font-display text-lg font-semibold tabular-nums text-brass">{money(p.net)}</p>
                                </div>
                                <PayoutBadge status={p.status} />
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ----------------- Verification -----------------
function VerificationRow({ icon: Icon, title, done, sub }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 p-3">
            <div className="flex items-center gap-3">
                <span className={cx("flex h-8 w-8 items-center justify-center rounded-full", done ? "bg-success/15 text-success" : "bg-warning/10 text-warning")}>
                    <Icon className="h-4 w-4" />
                </span>
                <div>
                    <p className="text-sm font-medium">{title}</p>
                    <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
            </div>
            {done
                ? <span className="inline-flex items-center gap-1 text-xs font-medium text-success"><CheckCircle2 className="h-3.5 w-3.5" />Done</span>
                : <span className="text-xs font-medium text-warning">Pending</span>}
        </div>
    );
}

function VerificationTab({ user, verified, config, onManage }) {
    const stripeReal = config?.stripe_connect_real;
    const twilioReal = config?.twilio_enabled;
    const sellerStatus = user.seller_status || "NOT_STARTED";

    return (
        <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
            <Card className="space-y-3 p-6">
                <div className="flex items-center justify-between">
                    <h3 className="font-display font-semibold">Account verification</h3>
                    {verified && <span className="inline-flex items-center gap-1 text-xs font-medium text-success"><ShieldCheck className="h-3.5 w-3.5" />Verified seller</span>}
                </div>
                <VerificationRow icon={Mail} title="Email verified" done={user.email_verified} sub="Confirms your account and enables notifications." />
                <VerificationRow icon={Phone} title="Phone verified" done={user.phone_verified} sub={twilioReal ? "Verified via SMS (Twilio)." : "Verified via SMS — currently in test mode."} />
                <VerificationRow icon={Store} title="Seller identity (Stripe Connect)" done={verified} sub={stripeReal ? "KYC + payouts via Stripe Connect." : "Identity & payouts — currently simulated."} />
                <Button className="mt-2 rounded-[10px]" onClick={onManage} data-testid="seller-manage-verification">
                    {verified ? "Manage verification" : "Complete verification"}<ArrowRight className="ml-2 h-4 w-4" />
                </Button>
            </Card>

            <Card className="space-y-3 p-6">
                <div className="flex items-center gap-2"><Store className="h-4 w-4 text-electric" /><h3 className="font-display font-semibold">Seller status</h3></div>
                <div className="rounded-lg border border-border bg-secondary/30 p-4 text-center">
                    <p className="font-display text-xl font-bold">{sellerStatus.replace(/_/g, " ")}</p>
                </div>
                {!stripeReal && (
                    <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
                        <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>Stripe Connect runs in <strong>simulated mode</strong>. Verification and payouts complete instantly for testing. Add live keys to enable real KYC and bank transfers — no code changes needed.</span>
                    </div>
                )}
                <p className="text-xs text-muted-foreground">A {config?.platform_fee_pct ?? 8}% marketplace fee is deducted from each sale; the remainder is paid out to your connected account.</p>
            </Card>
        </div>
    );
}


// ----------------- Profile (public seller page editor) -----------------
function ImageUploadField({ label, value, onChange, aspect = "banner", testId }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const pick = async (e) => {
        const f = (e.target.files || [])[0];
        if (!f) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", f);
        try { const { data } = await api.post("/uploads/image", fd); onChange(data.url); }
        catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        finally { setBusy(false); if (ref.current) ref.current.value = ""; }
    };
    return (
        <div className="space-y-2">
            <Label>{label}</Label>
            <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testId}-input`} />
            <div className={cx("group relative overflow-hidden rounded-xl border border-border bg-secondary/40", aspect === "avatar" ? "h-24 w-24" : "h-28 w-full")}>
                {value ? (
                    <img src={value} alt={label} className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-6 w-6" /></div>
                )}
            </div>
            <div className="flex gap-2">
                <Button type="button" size="sm" variant="secondary" className="rounded-[10px]" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-button`}>
                    {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}Upload
                </Button>
                {value && <Button type="button" size="sm" variant="ghost" className="rounded-[10px]" onClick={() => onChange("")} data-testid={`${testId}-remove`}><X className="mr-1.5 h-3.5 w-3.5" />Remove</Button>}
            </div>
        </div>
    );
}

function ProfileTab({ user }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [maxSpecialties, setMaxSpecialties] = useState(6);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setSocial = (k, v) => setForm((f) => ({ ...f, social: { ...f.social, [k]: v } }));

    const toggleSpecialty = (s) => setForm((f) => {
        const current = f.specialties || [];
        if (current.includes(s)) return { ...f, specialties: current.filter((x) => x !== s) };
        if (current.length >= maxSpecialties) { toast.message(`Up to ${maxSpecialties} specialties`); return f; }
        return { ...f, specialties: [...current, s] };
    });

    useEffect(() => {
        api.get("/seller/profile").then(({ data }) => setForm(data)).catch(() => { setForm(null); toast.error("Could not load profile"); });
        api.get("/specialties").then(({ data }) => { setSpecialtyOptions(data?.specialties || []); setMaxSpecialties(data?.max || 6); }).catch(() => {});
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/seller/profile", {
                store_name: form.store_name, bio: form.bio,
                location_city: form.location_city, location_state: form.location_state,
                avatar_url: form.avatar_url || "", banner_url: form.banner_url || "",
                social: form.social, handling_time: form.handling_time, return_policy: form.return_policy,
                specialties: form.specialties || [],
                contact_pref: form.contact_pref, notify_email: form.notify_email, notify_sms: form.notify_sms,
            });
            toast.success("Profile saved");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not save profile"); } finally { setBusy(false); }
    };

    if (!form) return <Skeleton className="h-96 w-full rounded-xl" />;
    const locked = form.locked || {};

    return (
        <div className="grid gap-5 lg:grid-cols-[1.3fr_0.7fr]">
            <div className="space-y-5">
                <Card className="space-y-5 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2"><UserCircle className="h-5 w-5 text-electric" /><h3 className="font-display font-semibold">Public profile</h3></div>
                        <Button asChild size="sm" variant="ghost" className="rounded-[10px] text-electric" data-testid="profile-view-public">
                            <Link to={`/seller/${user.id}`} target="_blank"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />View public page</Link>
                        </Button>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
                        <ImageUploadField label="Profile photo" value={form.avatar_url} onChange={(v) => set("avatar_url", v)} aspect="avatar" testId="profile-avatar" />
                        <ImageUploadField label="Store banner / cover image" value={form.banner_url} onChange={(v) => set("banner_url", v)} aspect="banner" testId="profile-banner" />
                    </div>

                    <div className="space-y-2"><Label htmlFor="store">Display / store name</Label><Input id="store" value={form.store_name || ""} onChange={(e) => set("store_name", e.target.value)} data-testid="profile-store-name" /></div>
                    <div className="space-y-2"><Label htmlFor="bio">Bio / about</Label><Textarea id="bio" rows={4} value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} placeholder="Tell buyers who you are and what you sell." data-testid="profile-bio" /></div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2"><Label htmlFor="city">City</Label><Input id="city" value={form.location_city || ""} onChange={(e) => set("location_city", e.target.value)} data-testid="profile-city" /></div>
                        <div className="space-y-2"><Label htmlFor="state">State / region</Label><Input id="state" value={form.location_state || ""} onChange={(e) => set("location_state", e.target.value)} data-testid="profile-state" /></div>
                    </div>

                    <div className="space-y-2" data-testid="profile-specialties">
                        <div className="flex items-center justify-between">
                            <Label>Specialties</Label>
                            <span className="text-xs text-muted-foreground">{(form.specialties || []).length}/{maxSpecialties} selected</span>
                        </div>
                        <p className="text-xs text-muted-foreground">What does your store focus on? These show on your public profile and help buyers find you in search.</p>
                        <div className="flex flex-wrap gap-2 pt-1">
                            {specialtyOptions.map((s) => {
                                const active = (form.specialties || []).includes(s);
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        data-testid={`specialty-chip-${s}`}
                                        onClick={() => toggleSpecialty(s)}
                                        aria-pressed={active}
                                        className={cx(
                                            "rounded-full border px-3 py-1.5 text-sm transition-colors duration-150",
                                            active
                                                ? "border-brass bg-brass/15 text-brass"
                                                : "border-border bg-transparent text-muted-foreground hover:border-electric/40 hover:text-foreground"
                                        )}
                                    >
                                        {s}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </Card>

                <Card className="space-y-4 p-6">
                    <h3 className="font-display font-semibold">Selling details</h3>
                    <div className="space-y-2"><Label htmlFor="handling">Shipping handling time</Label><Input id="handling" value={form.handling_time || ""} onChange={(e) => set("handling_time", e.target.value)} placeholder="Ships within 1-2 business days" data-testid="profile-handling" /></div>
                    <div className="space-y-2"><Label htmlFor="returns">Return policy</Label><Textarea id="returns" rows={2} value={form.return_policy || ""} onChange={(e) => set("return_policy", e.target.value)} placeholder="e.g. 30-day returns on unused items." data-testid="profile-return-policy" /></div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2"><Label>Instagram</Label><Input value={form.social?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} placeholder="@handle" data-testid="profile-instagram" /></div>
                        <div className="space-y-2"><Label>TikTok</Label><Input value={form.social?.tiktok || ""} onChange={(e) => setSocial("tiktok", e.target.value)} placeholder="@handle" data-testid="profile-tiktok" /></div>
                        <div className="space-y-2"><Label>Website</Label><Input value={form.social?.website || ""} onChange={(e) => setSocial("website", e.target.value)} placeholder="https://" data-testid="profile-website" /></div>
                    </div>
                    <div className="space-y-2">
                        <Label>Contact preference</Label>
                        <Select value={form.contact_pref} onValueChange={(v) => set("contact_pref", v)}>
                            <SelectTrigger data-testid="profile-contact-pref"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="messages_only">Platform messages only</SelectItem>
                                <SelectItem value="messages_and_email">Messages & email notifications</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
                        <div className="flex items-center justify-between"><Label htmlFor="ne" className="text-sm font-normal">Email notifications (bids, sales, disputes)</Label><Switch id="ne" checked={!!form.notify_email} onCheckedChange={(v) => set("notify_email", v)} data-testid="profile-notify-email" /></div>
                        <div className="flex items-center justify-between"><Label htmlFor="ns" className="text-sm font-normal">SMS notifications</Label><Switch id="ns" checked={!!form.notify_sms} onCheckedChange={(v) => set("notify_sms", v)} data-testid="profile-notify-sms" /></div>
                    </div>
                    <Button className="rounded-[10px]" onClick={save} disabled={busy} data-testid="profile-save-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}Save profile</Button>
                </Card>
            </div>

            {/* Locked trust fields */}
            <Card className="space-y-3 p-6" data-testid="profile-locked-fields">
                <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-warning" /><h3 className="font-display font-semibold">Locked for trust &amp; safety</h3></div>
                <p className="text-xs text-muted-foreground">These come from your verified identity and can&apos;t be edited here.</p>
                <LockedRow label="Legal name (KYC)" value={locked.legal_name} />
                <LockedRow label="Login email" value={locked.email} />
                <LockedRow label="Verification phone" value={locked.phone || "—"} />
                <LockedRow label="Identity status" value={locked.verified_seller ? "Verified seller" : (locked.seller_status || "").replace(/_/g, " ")} good={locked.verified_seller} />
                <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
                    Payout method and tax information are managed under <span className="font-medium text-foreground">Verification</span> and are never shown publicly.
                </div>
            </Card>
        </div>
    );
}

function LockedRow({ label, value, good }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/20 p-3">
            <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="text-sm font-medium">{value}</p>
            </div>
            <Lock className={cx("h-3.5 w-3.5", good ? "text-success" : "text-muted-foreground")} />
        </div>
    );
}
